/**
 * sacrifice.ts — 제물 시스템 (Sprint 3)
 *
 * N강 도전 시, 보관함(keepInventory)에 있는 정확히 직전 등급(N-1)
 * 무기를 제물로 바쳐 성공률 보너스를 얻는다.
 *
 * 규칙:
 *   - 제물 1개(소모) = 성공률 +1%
 *   - 도전당 최대 BASE_CAP개 (7개, +7%)
 *   - 광고 시청 시 +AD_EXTRA개 추가 가능 → 절대 상한 ABSOLUTE_CAP개 (+10%)
 *   - 성공/파괴 시 보너스 리셋 (같은 레벨에서 누적 유지)
 *
 * 순수 로직만. 브라우저/PIXI 금지.
 */

import type { KeepInventoryState } from './keepInventory.js';

// ────────────────────────────────────────────────
//  상수
// ────────────────────────────────────────────────

/** 광고 없이 기본 제물 상한 */
export const SACRIFICE_BASE_CAP = 7;
/** 광고 한 번당 추가 제물 슬롯 */
export const SACRIFICE_AD_EXTRA = 3;
/** 절대 상한 (BASE + AD) */
export const SACRIFICE_ABSOLUTE_CAP = 10;
/**
 * 현재 강화 레벨 기준 제물 1개당 성공률 보너스 (소수)
 * ~21강: +3% / 22~24강: +2% / 25강 이상: +1%
 */
export function sacrificeBonusPer(weaponLevel: number): number {
    if (weaponLevel >= 25) return 0.01;
    if (weaponLevel >= 22) return 0.02;
    return 0.03;
}

// ────────────────────────────────────────────────
//  상태 타입
// ────────────────────────────────────────────────

export interface SacrificeState {
    /** 현재 제물 보너스 개수 (0~ABSOLUTE_CAP) */
    count: number;
    /** 광고로 추가된 슬롯 수 (0~AD_EXTRA) */
    adExtra: number;
    /** 어느 레벨에서 제물을 쌓고 있는지 (레벨 변경 시 리셋 기준) */
    forLevel: number;
}

export function emptySacrifice(currentLevel: number): SacrificeState {
    return { count: 0, adExtra: 0, forLevel: currentLevel };
}

// ────────────────────────────────────────────────
//  유틸리티
// ────────────────────────────────────────────────

/** 현재 제물 수 상한 — 기본 BASE_CAP, 광고 시청 시 adExtra만큼 확장 (절대 상한 ABSOLUTE_CAP) */
export function sacrificeCap(state: SacrificeState): number {
    return Math.min(SACRIFICE_BASE_CAP + state.adExtra, SACRIFICE_ABSOLUTE_CAP);
}

/** 현재 성공률 보너스 (소수, 예: 0.07 = +7%) */
export function sacrificeBonus(state: SacrificeState, weaponLevel: number): number {
    return state.count * sacrificeBonusPer(weaponLevel);
}

/** 보관함에서 제물로 쓸 수 있는 무기 목록 (레벨 = weaponLevel - 1) 인덱스 배열 반환 */
export function sacrificeTargetIndices(
    keepInv: KeepInventoryState,
    weaponLevel: number,
): number[] {
    const targetLevel = weaponLevel - 1;
    if (targetLevel < 1) return [];
    return keepInv.weapons
        .map((w, i) => ({ ...w, i }))
        .filter((w) => w.level === targetLevel)
        .map((w) => w.i);
}

/**
 * 제물 바치기: 보관함에서 index 무기를 제거하고 count+1.
 * 슬롯 상한 초과 시 null 반환.
 * weaponLevel: 현재 강화 도전 레벨 (티어% 계산용, 검증용)
 */
export function addSacrifice(
    state: SacrificeState,
    keepInv: KeepInventoryState,
    keepIndex: number,
    _weaponLevel?: number,
): { sacrificeState: SacrificeState; keepInv: KeepInventoryState } | null {
    if (state.count >= sacrificeCap(state)) return null;
    if (keepIndex < 0 || keepIndex >= keepInv.weapons.length) return null;

    const newKeep: KeepInventoryState = {
        ...keepInv,
        weapons: keepInv.weapons.filter((_, i) => i !== keepIndex),
    };
    const newState: SacrificeState = { ...state, count: state.count + 1 };
    return { sacrificeState: newState, keepInv: newKeep };
}

/**
 * 광고 시청으로 제물 수량을 직접 +AD_EXTRA 증가시킨다.
 * (무기를 바치지 않아도 광고로 제물 3개를 바친 효과 → 즉시 보너스 상승.)
 * 이미 절대 상한이면 null 반환.
 */
export function applyAdExtra(
    state: SacrificeState,
): SacrificeState | null {
    if (state.adExtra >= SACRIFICE_AD_EXTRA) return null; // 광고 보너스는 도전당 1회
    if (state.count >= SACRIFICE_ABSOLUTE_CAP) return null;
    const newCount = Math.min(state.count + SACRIFICE_AD_EXTRA, SACRIFICE_ABSOLUTE_CAP);
    return { ...state, count: newCount, adExtra: state.adExtra + SACRIFICE_AD_EXTRA };
}

/**
 * 성공 또는 파괴 시 보너스 리셋 + 새 레벨 세팅.
 */
export function resetSacrifice(newLevel: number): SacrificeState {
    return emptySacrifice(newLevel);
}
